import React from 'react';
import { ShieldCheck, Shield, AlertCircle, Clock } from 'lucide-react';

/**
 * Truthful verification badge driven by `verification_summary` from the
 * backend. Possible states:
 *   - `verified`  → all submitted credentials have been checked
 *   - `partial`   → at least one credential is verified AND at least one
 *                   other is still pending or rejected
 *   - `pending`   → credentials submitted but not yet checked
 *   - `rejected`  → all submitted credentials were rejected
 *   - `none`      → no credentials submitted
 *
 * NEVER derives verification from availability or `profile_visibility`.
 */
export function VerificationBadge({ summary, size = 'md', testIdPrefix = '' }) {
  const base =
    size === 'sm'
      ? 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium'
      : 'inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm';
  const icon = size === 'sm' ? 'h-3 w-3' : 'h-5 w-5';

  const prefix = testIdPrefix ? `${testIdPrefix}-` : '';

  switch (summary) {
    case 'verified':
      return (
        <span
          className={`${base} bg-green-50 border border-green-200 text-green-700`}
          title="OnSiteHand has checked the evidence submitted for every credential on this profile. Not a guarantee of workmanship, conduct, availability or suitability."
          data-testid={`${prefix}verification-badge-verified`}
        >
          <ShieldCheck className={icon} />
          Credentials verified
        </span>
      );
    case 'partial':
      return (
        <span
          className={`${base} bg-amber-50 border border-amber-200 text-amber-700`}
          title="OnSiteHand has checked the evidence for at least one credential on this profile. One or more other credentials are still being checked."
          data-testid={`${prefix}verification-badge-partial`}
        >
          <ShieldCheck className={icon} />
          Some credentials verified
        </span>
      );
    case 'pending':
      return (
        <span
          className={`${base} bg-blue-50 border border-blue-200 text-blue-700`}
          title="Credentials have been submitted and are awaiting OnSiteHand review."
          data-testid={`${prefix}verification-badge-pending`}
        >
          <Clock className={icon} />
          Verification pending
        </span>
      );
    case 'rejected':
      return (
        <span
          className={`${base} bg-rose-50 border border-rose-200 text-rose-700`}
          title="OnSiteHand reviewed the submitted evidence and could not verify it."
          data-testid={`${prefix}verification-badge-rejected`}
        >
          <AlertCircle className={icon} />
          Evidence could not be verified
        </span>
      );
    case 'none':
    default:
      return (
        <span
          className={`${base} bg-slate-100 border border-slate-200 text-slate-600`}
          title="This tradesperson has not submitted any credentials for OnSiteHand review."
          data-testid={`${prefix}verification-badge-none`}
        >
          <Shield className={icon} />
          Not verified by OnSiteHand
        </span>
      );
  }
}

export default VerificationBadge;
